import React, { Fragment } from 'react'
import { connect } from 'react-redux'
import styles from './CollectionItems.scss'
import { collectionsMessages } from 'features/homepage/Collections/intlMessages'
import { LoadExistingContainer, loadExistingMessages } from 'features/search/LoadExisting'
import { collectionItemActions, collectionItemSelectors } from 'data/resources/collectionItems'
import { userSelectors } from 'data/resources/users'
import { Route } from 'data/records'
import { loadGeoJSON } from 'lib/api/embeds/regional'
import { TRouteItemContainer } from 'common/TRouteItem'
import { Icon } from 'common/Icon'
import { Card } from 'common/styleguide/Card'
import { CTA } from 'common/styleguide/CTA'
import { Msg, commonVerbs } from 'lib/localization'
import { FormattedMessage as T } from 'react-intl'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

export class _CollectionItems extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      deletePending: false,
      pendingOrder: null
    }
  }

  deleteClicked = (troute) => {
    const { collectionItems, deleteCollectionItem } = this.props
    const objectType = troute instanceof Route ? 'route' : 'trip'
    const collectionItem = collectionItems.find(ci => ci.itemId === troute.id && ci.itemType === objectType)

    this.setState({ deletePending: true })
    deleteCollectionItem(collectionItem.id).then(
      () => this.setState({ deletePending: false })
    )
  }

  onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const { collectionItems, reorder, collection } = this.props

    // Move the id from the old position to the new one
    let order = collectionItems.map(ci => ci.id).toArray()
    const [removed] = order.splice(result.source.index, 1)
    order.splice(result.destination.index, 0, removed)
    order = order.map((id, idx) => [id, idx + 1])

    this.setState({ pendingOrder: order.map(([id]) => id) })
    reorder({ collectionId: collection.id, order }).finally(() => {
      this.setState({ pendingOrder: null })
    })
  }

  render () {
    const { collection, collapse, itemTypes, addTRoutes, currentUserIsOwner, userById, intl } = this.props
    let { troutes } = this.props
    const { deletePending, pendingOrder } = this.state
    const owner = userById(collection.userId)

    if (troutes.size === 0) {
      return <Empty collapse={collapse} currentUserIsOwner={currentUserIsOwner} addTRoutes={addTRoutes} owner={owner} />
    }

    if (pendingOrder) {
      troutes = troutes.sortBy((_, k) => pendingOrder.indexOf(k))
    }

    return (
      <Card noPadding>
        <DragDropContext onDragEnd={this.onDragEnd}>
          <Droppable droppableId='droppable'>
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}>
                {troutes.valueSeq().map((r, idx) => r &&
                <DraggableTRouteItemContainer
                  key={`${r.name + r.id}`}
                  noBadge={itemTypes.length === 1}
                  r={r}
                  index={idx}
                  editable={currentUserIsOwner}
                  deletable={!deletePending && currentUserIsOwner}
                  deleteTitle={intl.formatMessage({ ...collectionsMessages.removeFromCollection })}
                  deleteClicked={currentUserIsOwner && !deletePending && this.deleteClicked}
                  hoverDisabled={snapshot.isDraggingOver} />
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Card>
    )
  }
}

const DraggableTRouteItemContainer = ({ noBadge, r, index, editable, deletable, deleteTitle, deleteClicked, hoverDisabled }) => {
  return (
    <Draggable draggableId={`draggableTRouteItemContainer-${r.id}-${index}`} isDragDisabled={!editable} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.dragHandleProps}
          {...provided.draggableProps}>
          <TRouteItemContainer
            key={`draggableCI-${r.id}`}
            noBadge={noBadge}
            r={r}
            editable={editable}
            deletable={deletable}
            draggable={editable}
            deleteTitle={deleteTitle}
            deleteClicked={deleteClicked}
            isDragging={snapshot.isDragging}
            hoverDisabled={hoverDisabled}
            dragHandleProps={provided.dragHandleProps} />
        </div>
      )}
    </Draggable>
  )
}

class Empty extends React.PureComponent {
  state = {
    showImport: false
  }

  setShowImport = (showImport) => {
    this.setState({ showImport })
  }

  render () {
    const { collapse, currentUserIsOwner, addTRoutes, owner } = this.props
    const { showImport } = this.state

    return <Fragment>
      {showImport && <LoadExistingContainer
        loadMultiple={addTRoutes}
        onRequestClose={() => this.setShowImport(false)}
        submitLabel={collapse ? <Msg m={commonVerbs.add} /> : <Msg m={collectionsMessages.addToCollection} />} />}
      <Card className={styles.emptyContainer}>
        <Icon size={100} i='no-collection-items' />
        {currentUserIsOwner
          ? <Fragment>
            <T
              id='CollectionItems.importForMap'
              defaultMessage='Import routes and rides to populate the interactive map, stats and collection listing.' />
            <CTA secondary onClick={() => this.setShowImport(true)}><Msg m={loadExistingMessages.importRoutesAndRides} /></CTA>
          </Fragment>
          : <T id='CollectionItems.noItems'
            defaultMessage='{name} has not added any items to this collection'
            values={{ name: owner.name }} />}
      </Card>
    </Fragment>
  }
}

const mapStateToProps = (state, ownProps) => {
  const collectionId = ownProps.collection.id
  return {
    collectionItems: collectionItemSelectors.byCollectionId(state, collectionId),
    userById: id => userSelectors.find(state, id)
  }
}

const mapDispatchToProps = {
  deleteCollectionItem: collectionItemActions.delete,
  reorder: collectionItemActions.reorder,
  loadGeoJSON: loadGeoJSON
}

export const CollectionItems = connect(
  mapStateToProps,
  mapDispatchToProps
)(_CollectionItems)
